// Directory — employee list + editable org chart.
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../shared/api/client";
import { PageHeader } from "../../shared/components/PageHeader";
import { Empty, Loading } from "../../shared/components/Empty";
import { Pill } from "../../shared/components/Pill";
import { toast } from "../../shared/stores/toasts";
import { useAuth } from "../../shared/stores/auth";

// ─── Types ────────────────────────────────────────────────────────────────────
type DirUser = {
  id: number;
  email: string;
  name: string;
  username?: string | null;
  department_name: string | null;
  department_id?: number | null;
  position_name: string | null;
  status: string;
};

type DeptNode = {
  id: number;
  code: string;
  name: string;
  level: number;
  parent_id: number | null;
  children: DeptNode[];
  manager_id?: number | null;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function usersInDept(users: DirUser[], deptId: number): DirUser[] {
  return users.filter((u) => u.department_id === deptId && u.status === "ACTIVE");
}

// ─── Org Node (recursive) ────────────────────────────────────────────────────
function OrgNode({
  node,
  users,
  canEdit,
  onRename,
  onAddChild,
  onDelete,
}: {
  node: DeptNode;
  users: DirUser[];
  canEdit: boolean;
  onRename: (id: number, name: string) => Promise<void>;
  onAddChild: (parentId: number) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState(node.name);
  const [expanded, setExpanded] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const members = usersInDept(users, node.id);

  const startEdit = () => {
    setEditVal(node.name);
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 30);
  };

  const commitEdit = async () => {
    setEditing(false);
    if (editVal.trim() && editVal !== node.name) {
      await onRename(node.id, editVal.trim());
    }
  };

  const nodeColor = node.level === 1 ? "var(--p-500)" : node.level === 2 ? "var(--p-400)" : "var(--p-300)";
  const hasChildren = node.children.length > 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 160 }}>
      {/* Department card */}
      <div
        style={{
          border: `2px solid ${nodeColor}`,
          borderRadius: 10,
          background: "var(--bg)",
          boxShadow: "0 1px 6px rgba(0,0,0,.06)",
          minWidth: 148,
          maxWidth: 200,
        }}
      >
        {/* Header */}
        <div
          style={{
            background: nodeColor,
            borderRadius: "8px 8px 0 0",
            padding: "6px 10px",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          {editing ? (
            <input
              ref={inputRef}
              value={editVal}
              onChange={(e) => setEditVal(e.target.value)}
              onBlur={commitEdit}
              onKeyDown={(e) => { if (e.key === "Enter") commitEdit(); if (e.key === "Escape") setEditing(false); }}
              style={{
                flex: 1, background: "transparent", border: "none", outline: "none",
                color: "#fff", fontSize: 13, fontWeight: 600,
              }}
            />
          ) : (
            <span
              style={{ flex: 1, color: "#fff", fontSize: 13, fontWeight: 600, cursor: canEdit ? "pointer" : "default" }}
              onDoubleClick={canEdit ? startEdit : undefined}
              title={canEdit ? "더블클릭으로 이름 변경" : undefined}
            >
              {node.name}
            </span>
          )}
          {hasChildren && (
            <button
              onClick={() => setExpanded((v) => !v)}
              style={{ background: "none", border: "none", color: "rgba(255,255,255,.8)", cursor: "pointer", padding: 0, lineHeight: 1 }}
            >
              {expanded ? "▲" : "▼"}
            </button>
          )}
        </div>

        {/* Members */}
        <div style={{ padding: "6px 8px" }}>
          {members.length === 0 ? (
            <div style={{ fontSize: 11, color: "var(--text-3)", textAlign: "center", padding: "4px 0" }}>멤버 없음</div>
          ) : (
            members.slice(0, 4).map((u) => (
              <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "2px 0" }}>
                <div
                  className="t-av"
                  style={{ width: 22, height: 22, fontSize: 9, flexShrink: 0, background: "var(--p-400)" }}
                >
                  {u.name.slice(0, 2)}
                </div>
                <div style={{ flex: 1, overflow: "hidden" }}>
                  <div style={{ fontSize: 12, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {u.name}
                  </div>
                  {u.position_name && (
                    <div style={{ fontSize: 10, color: "var(--text-3)" }}>{u.position_name}</div>
                  )}
                </div>
              </div>
            ))
          )}
          {members.length > 4 && (
            <div style={{ fontSize: 11, color: "var(--text-3)", textAlign: "center" }}>+{members.length - 4}명</div>
          )}
          {members.length > 0 && (
            <div style={{ fontSize: 11, color: "var(--text-3)", textAlign: "right", paddingTop: 2 }}>
              총 {members.length}명
            </div>
          )}
        </div>

        {/* Edit actions */}
        {canEdit && (
          <div style={{ borderTop: "1px solid var(--border)", padding: "4px 6px", display: "flex", gap: 4 }}>
            <button
              onClick={() => onAddChild(node.id)}
              title="하위 부서 추가"
              style={{ flex: 1, background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "var(--text-3)", padding: "2px 0" }}
            >
              + 하위
            </button>
            <button
              onClick={startEdit}
              title="이름 변경"
              style={{ flex: 1, background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "var(--text-3)", padding: "2px 0" }}
            >
              ✏️
            </button>
            {members.length === 0 && node.children.length === 0 && (
              <button
                onClick={() => onDelete(node.id)}
                title="삭제"
                style={{ flex: 1, background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "var(--danger, #ef4444)", padding: "2px 0" }}
              >
                🗑️
              </button>
            )}
          </div>
        )}
      </div>

      {/* Children */}
      {expanded && node.children.length > 0 && (
        <>
          {/* Vertical connector */}
          <div style={{ width: 2, height: 20, background: "var(--border)" }} />
          {/* Horizontal bar */}
          {node.children.length > 1 && (
            <div style={{ position: "relative", display: "flex", alignItems: "flex-start", justifyContent: "center" }}>
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: "calc(50% - " + (node.children.length > 1 ? "50%" : "1px") + ")",
                  right: "calc(50% - " + (node.children.length > 1 ? "50%" : "1px") + ")",
                  height: 2,
                  background: "var(--border)",
                  pointerEvents: "none",
                }}
              />
            </div>
          )}
          <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
            {node.children.map((child) => (
              <div key={child.id} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div style={{ width: 2, height: 20, background: "var(--border)" }} />
                <OrgNode
                  node={child}
                  users={users}
                  canEdit={canEdit}
                  onRename={onRename}
                  onAddChild={onAddChild}
                  onDelete={onDelete}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── List View ────────────────────────────────────────────────────────────────
function ListView({ q, setQ }: { q: string; setQ: (v: string) => void }) {
  const [items, setItems] = useState<DirUser[] | null>(null);

  useEffect(() => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    params.set("size", "50");
    api
      .get<{ data: DirUser[] }>(`/users?${params}`)
      .then((r) => setItems(r.data.data))
      .catch(() => setItems([]));
  }, [q]);

  return (
    <>
      <div style={{ padding: "0 28px" }}>
        <input
          className="t-input"
          placeholder="이름·아이디·부서로 검색…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ maxWidth: 400 }}
        />
      </div>
      <div className="t-grid cols-3" style={{ paddingTop: 16 }}>
        {items === null && <Loading />}
        {items?.length === 0 && <Empty msg="결과가 없습니다" />}
        {items?.map((u) => (
          <div
            key={u.id}
            className="t-card"
            style={{ flexDirection: "row", alignItems: "center", gap: 12 }}
          >
            <div
              className="t-av"
              style={{
                width: 42, height: 42, fontSize: 14,
                background: "linear-gradient(135deg, var(--p-400), var(--p-600))",
              }}
            >
              {u.name.slice(0, 2)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{u.name}</div>
              <div style={{ fontSize: 12, color: "var(--text-3)" }}>
                {u.username ? `${u.username}@timple.me` : u.email}
              </div>
              <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 2 }}>
                {u.department_name} · {u.position_name}
              </div>
            </div>
            <Pill kind={u.status === "ACTIVE" ? "ok" : "muted"}>{u.status}</Pill>
          </div>
        ))}
      </div>
    </>
  );
}

// ─── Org Chart View ───────────────────────────────────────────────────────────
function OrgChartView() {
  const [tree, setTree] = useState<DeptNode[] | null>(null);
  const [users, setUsers] = useState<DirUser[]>([]);
  const { manifest } = useAuth();
  const canEdit = manifest?.roles.includes("SUPER_ADMIN") ?? false;

  const reload = () =>
    Promise.all([
      api.get<DeptNode[]>("/iam/departments/tree"),
      api.get<{ data: DirUser[] }>("/users?size=200&include_dept=1"),
    ])
      .then(([treeRes, userRes]) => {
        setTree(treeRes.data);
        setUsers(userRes.data.data ?? []);
      })
      .catch(() => setTree([]));

  useEffect(() => { reload(); }, []);

  const handleRename = async (id: number, name: string) => {
    try {
      await api.patch(`/iam/departments/${id}`, { name });
      toast.success("부서명 변경됨");
      reload();
    } catch (e: any) {
      toast.error("변경 실패", e?.response?.data?.detail);
    }
  };

  const handleAddChild = async (parentId: number) => {
    const name = prompt("새 하위 부서 이름을 입력하세요");
    if (!name?.trim()) return;
    try {
      await api.post("/iam/departments", {
        code: `DEPT_${Date.now()}`,
        name: name.trim(),
        parent_id: parentId,
      });
      toast.success("하위 부서 추가됨");
      reload();
    } catch (e: any) {
      toast.error("추가 실패", e?.response?.data?.detail);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("이 부서를 삭제하시겠습니까?")) return;
    try {
      await api.delete(`/iam/departments/${id}`);
      toast.success("삭제됨");
      reload();
    } catch (e: any) {
      toast.error("삭제 실패", e?.response?.data?.detail);
    }
  };

  const addRoot = async () => {
    const name = prompt("최상위 부서 이름을 입력하세요");
    if (!name?.trim()) return;
    try {
      await api.post("/iam/departments", {
        code: `DEPT_${Date.now()}`,
        name: name.trim(),
        parent_id: null,
      });
      toast.success("부서 추가됨");
      reload();
    } catch (e: any) {
      toast.error("추가 실패", e?.response?.data?.detail);
    }
  };

  if (tree === null) return <div style={{ padding: 40 }}><Loading /></div>;
  if (tree.length === 0) return <Empty msg="등록된 부서가 없습니다" />;

  return (
    <div>
      {canEdit && (
        <div style={{ padding: "0 28px 12px", display: "flex", gap: 8 }}>
          <button className="t-btn solid" onClick={addRoot}>+ 최상위 부서 추가</button>
          <span style={{ fontSize: 12, color: "var(--text-3)", alignSelf: "center" }}>
            부서 이름을 더블클릭하면 수정할 수 있습니다
          </span>
        </div>
      )}
      <div
        style={{
          overflowX: "auto",
          overflowY: "auto",
          padding: "16px 28px 40px",
          display: "flex",
          gap: 40,
          minHeight: 300,
        }}
      >
        {tree.map((root) => (
          <OrgNode
            key={root.id}
            node={root}
            users={users}
            canEdit={canEdit}
            onRename={handleRename}
            onAddChild={handleAddChild}
            onDelete={handleDelete}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function DirectoryPage() {
  const [params, setParams] = useSearchParams();
  const view = (params.get("filter") as "list" | "org") ?? "list";
  const setView = (v: string) => setParams({ filter: v });
  const [q, setQ] = useState("");

  return (
    <>
      <PageHeader title="임직원" subtitle="조직도 · 검색" />

      {/* Tab switcher */}
      <div style={{ padding: "0 28px 16px", display: "flex", gap: 4 }}>
        {(["list", "org"] as const).map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            style={{
              padding: "6px 18px",
              borderRadius: 8,
              border: "1px solid var(--border)",
              background: view === v ? "var(--p-500)" : "var(--bg-2)",
              color: view === v ? "#fff" : "var(--text-2)",
              fontWeight: view === v ? 600 : 400,
              cursor: "pointer",
              fontSize: 13,
            }}
          >
            {v === "list" ? "👥 목록" : "🏢 조직도"}
          </button>
        ))}
      </div>

      {view === "list" ? <ListView q={q} setQ={setQ} /> : <OrgChartView />}
    </>
  );
}
